<template>
  <div class="page-wrapper">
    <div class="sticky-header">
      <Header :title="'Профиль'" />
    </div>

    <div class="scrollable-content">
      <ProfileSkeleton v-if="isLoadingProfile" />
      <div v-else class="layout">

        <SettingsSidebar
          :active-item="selectedSection"
          @change="handleChange"
        />

        <div class="profile-main">
          <h1 class="profile-title">{{ currentTitle }}</h1>

          <transition name="fade">
            <div v-if="loadingDelete" class="loading-spinner">Удаляем аккаунт...</div>
          </transition>
          
          <ProfileGeneral
            v-if="selectedSection === 'general'"
            :user="user"
            @update:user="updateUserFields"
            @updated="handleGeneralUpdated"
          />

          <ProfileSkills
            v-if="selectedSection === 'skills'"
            :initialHashtags="user.hashtags || []"
            :user-id="user.user_id"
            @saved="handleSkillsSaved"
          />

          <ProfileCompany
            v-if="selectedSection === 'company'"
            :current-company="user.current_company"
            :companies="user.companies"
            @open-company-drawer="openCompanyDrawerRegular"
            @open-company-drawer-premium = "openCompanyDrawerPremium"
            @open-premium="handleOpenPaywall"
            @company-removed="onCompanyRemoved"
            @update:company="company = $event"
          />

          <ProfileEmployerContent
            v-if="selectedSection === 'employer-content'"
            :model-value="user.is_employer"
            @update:model-value="handleEmployerContentChange"
            @change="handleEmployerContentChange"
          />

          <ProfileSocial
            v-if="selectedSection === 'social'" 
            :initialSocial="user.social"
          />

          <ProfileProfessionalLinks
            v-if="selectedSection === 'professional'"
            :initial-links="user.working_profiles"
            :user-id="user.user_id"
          />

          <ProfileOther 
          v-if="selectedSection === 'other'"
          />

          <ProfilePremium
            v-if="selectedSection === 'premium'"
            :user-id="user.user_id"
            @clickPremium="handleOpenPaywall"
          />

          <ProfileAccount
            v-if="selectedSection === 'account'"
            @contact-support="openSupportChat"
            @freeze-account="handleFreeze"
            @logout="logoutConfirm"
            @delete-account="confirmDelete"
            :user-id="userIdFromStorage"
            :provider="user.provider"
            :auth-email="user.auth_email"
            :phone-number="user.phone_number"
          />

          <ProfileResume
            v-if="selectedSection === 'resume'"
            :career="user.career || []"
            :education="user.education || []"
            :languages="user.languages || []"
            :user-id="user.user_id"
          />

        </div>

        <div
          v-if="selectedSection === 'general'"
          class="profile-right"
        >
          <AIBanner
            title="Улучшить резюме?"
            subtitle="Попросить помощи у нейронной сети красиво оформить твое резюме"
            :image="aiResumeImage"
            :loading="aiLoading1"
            @click="handleAIClick1"
          />

          <AIBanner
            title="Лень заполнять резюме?"
            subtitle="Просто загрузи свое резюме в pdf/doc формате, а дальше нейронная сеть все сделает сама"
            :image="pdfImage"
            :loading="aiLoading2"
            @click="handleAIClick2"
          />
        </div>

      </div>
    </div>

    <div class="sticky-footer">
      <MiniFooter />
    </div>

    <transition name="fade">
      <button
        v-if="hasChanges"
        class="floating-save-btn"
        @click="saveChanges"
      >
        Сохранить
      </button>
    </transition>

    <transition name="fade">
      <div v-if="showSnackbar" class="snackbar">{{ snackbarMessage }}</div>
    </transition>

    <ContactSupportDrawer
      :visible="showSupport"
      @close="showSupport = false"
      @snackbar="({message,type}) => showSnackbarMessage(message)"
    />

    <ModalConfirm
      :isVisible="showLogoutModal"
      titleText="Выйти из аккаунта?"
      messageText="Вы точно хотите выйти из аккаунта?"
      cancelText="Отмена"
      confirmText="Выйти"
      @close="showLogoutModal = false"
      @confirm="logout"
    />

    <ModalConfirm
      :isVisible="showDeleteModal"
      titleText="Удалить аккаунт?"
      messageText="Вы точно хотите удалить аккаунт?"
      cancelText="Отмена"
      confirmText="Удалить"
      @close="showDeleteModal = false"
      @confirm="deleteAccount"
    />

    <ModalConfirm
      :isVisible="showFreezeModal"
      titleText="Заморозить аккаунт?"
      messageText="Ваш профиль будет скрыт, но все данные сохранятся. Вы сможете восстановить аккаунт при следующем входе."
      cancelText="Отмена"
      confirmText="Заморозить"
      @close="showFreezeModal = false"
      @confirm="freezeAccount"
    />

    <ProfileCompanyAnimationDrawer 
      :visible="showCompanyDrawer"
      :isFullStackHRPremiumOpen="isPremiumModeForCompany"
      @close="showCompanyDrawer = false"
      @start-overlay="onStartOverlay"
      @stop-overlay="onStopOverlay"
      @company-added="onCompanyAdded"
    />

    <AIResumeUploadModal
      v-if="showAIModal"
      @close="showAIModal = false"
      @completed="handleResumeUploadCompleted"
    />

  </div> 

  <div v-if="isCompanyUpdating"  class="company-overlay">
    <div class="spinner"></div>
  </div>

  <teleport to="body">
    <Paywall v-if="showPaywall" @close="showPaywall = false" />
  </teleport>

</template>

<script setup>

import { ref, onMounted, computed, nextTick, watch } from 'vue';

import { getAuth, signOut } from 'firebase/auth';
import { useRouter } from 'vue-router';
import { useRoute } from "vue-router";
import { AnalyticsService, AnalyticsEvent } from "../utils/AnalyticsService";

import AIResumeUploadModal from "../components/AIResumeUploadModal.vue";
import AIBanner from "../components/AIBanner.vue";
import aiResumeImage from "../assets/ai_grapes.png";
import pdfImage from "../assets/ai_pdf.png";
import Header from '../components/ProfileHeader.vue';
import SettingsSidebar from '../components/SettingsSidebar.vue';
import MiniFooter from '../components/MiniFooter.vue';
import ModalConfirm from '../components/ModalConfirm.vue';
import UserService from '../api/UserService';
import User from '../models/User';
import PremiumService from "../api/PremiumService";

import Paywall from "../views/Paywall.vue";
import ProfileGeneral from '../components/profile/ProfileGeneral.vue';
import ProfileSkills from '../components/profile/ProfileSkills.vue';
import ProfileCompany from '../components/profile/ProfileCompany.vue';
import ProfileSocial from '../components/profile/ProfileSocial.vue';
import ProfileProfessionalLinks from '../components/profile/ProfileProfessionalLinks.vue';
import ProfileOther from '../components/profile/ProfileOther.vue';
import ProfileAccount from '../components/profile/ProfileAccount.vue';
import ProfileCompanyAnimationDrawer from '../components/profile/ProfileCompanyAnimationDrawer.vue';
import ProfileResume from '../components/profile/ProfileResume.vue';
import ProfilePremium from '../components/profile/ProfilePremium.vue';
import ProfileSkeleton from '../components/ProfileSkeleton.vue';
import ContactSupportDrawer from '../components/profile/ContactSupportDrawer.vue';
import ProfileEmployerContent from "../components/profile/ProfileEmployerContent.vue";

const isPremiumModeForCompany = ref(false);
const showPaywall = ref(false);
const route = useRoute();
const isCompanyUpdating = ref(false); 
const showSupport = ref(false);
const router = useRouter();
const userId = localStorage.getItem('user_id');
const user = ref(new User());
const selectedSection = ref('general');
const showSnackbar = ref(false);
const snackbarMessage = ref('');
const hasChanges = ref(false);
const showLogoutModal = ref(false);
const showDeleteModal = ref(false);

const showFreezeModal = ref(false);
const loadingFreeze = ref(false);
const employerContentEnabled = ref(false);

const showCompanyDrawer = ref(false);
const userIdFromStorage = localStorage.getItem('user_id');
const isLoadingProfile = ref(true);

const aiLoading1 = ref(false);
const aiLoading2 = ref(false);
const showAIModal = ref(false);

const allowedSections = [
  "general",
  "skills",
  "company",
  "employer-content",
  "resume",
  "social",
  "professional",
  "premium",
  "other",
  "account"
];

const sectionTitles = {

general:
  "Общее",

skills:
  "Профессиональные скиллы",

company: (user) => {

  const count =
    user.companies?.length || 0;

  if (count > 1) {
    return "Компании, с которыми вы работаете";
  }

  if (count === 1) {
    return "Компания, в которой вы работаете";
  }

  return "Добавьте компанию, в которой вы работаете";
},

"employer-content":
  "Размещение контента",

resume:
  "Резюме",

social:
  "Социальные сети",

professional:
  "Профессиональные ссылки",

premium:
  "Подписка",

other:
  "Прочее",

account:
  "Аккаунт"

};

const onStartOverlay = () => {
  console.log('[overlay] start');
  isCompanyUpdating.value = true;
};

const handleEmployerContentChange = async (enabled) => {
  try {
    await UserService.updateEmployerStatus(enabled);

    user.value.is_employer = enabled;

    localStorage.setItem(
      "user_profile",
      JSON.stringify(user.value)
    );

    if (enabled) {
      showSnackbarMessage(
        "Теперь вы можете публиковать вакансии и контент от имени компании"
      );
    } else {
      showSnackbarMessage(
        "Размещение контента от имени компании отключено"
      );
    }

    console.log(
      "✅ is_employer обновлён:",
      enabled
    );

  } catch (error) {
    console.error(
      "❌ Ошибка обновления is_employer:",
      error
    );

    showSnackbarMessage(
      "Не удалось изменить настройку. Попробуйте ещё раз"
    );
  }
};

const onStopOverlay = () => {
  console.log('[overlay] stop');
  isCompanyUpdating.value = false;
};

const onCompanyAdded = (fullCompany) => {
  user.value.current_company = fullCompany;

  localStorage.setItem("user_profile", JSON.stringify(user.value));

  snackbarMessage.value = `Компания обновлена: ${fullCompany.name}`;
  showSnackbar.value = true;
};

const currentTitle = computed(() => {
  const key = selectedSection.value;
  const entry = sectionTitles[key];
  if (typeof entry === "function") return entry(user.value);
  return entry || "";
});


const handleChange = (key) => {
  if (!allowedSections.includes(key)) return;

  router.replace({
    query: {
      ...route.query,
      section: key
    }
  });
};

watch(
  () => route.query.section,
  (section) => {
    if (typeof section === "string" && allowedSections.includes(section)) {
      selectedSection.value = section;
    } else {
      selectedSection.value = "general";
    }
  },
  { immediate: true }
);

async function loadProfile() {
  const T0 = performance.now();
  console.log('[profile] start load, isLoadingProfile:', isLoadingProfile.value); 

  const cached = localStorage.getItem('user_profile');
  if (cached) {
    try { user.value = JSON.parse(cached); } catch {}
  }

  try {
    const fetched = await UserService.getProfile(userId);
    user.value = fetched;
    localStorage.setItem('user_profile', JSON.stringify(fetched));
  } catch (e) {
    console.error('❌ Ошибка при загрузке профиля:', e);
    showSnackbarMessage('Не удалось загрузить профиль');
  } finally {

    AnalyticsService.log(AnalyticsEvent.OPEN_PROFILE);

    const MIN_SKELETON_MS = 400;
    const dt = performance.now() - T0;
    const delay = Math.max(0, MIN_SKELETON_MS - dt);
    setTimeout(() => {
      isLoadingProfile.value = false;
      console.log('[profile] finish load, isLoadingProfile:', isLoadingProfile.value);
    }, delay);
  }
}

function updateUserFields(patch) {
  Object.assign(user.value, patch);
  localStorage.setItem('user_profile', JSON.stringify(user.value));
}

onMounted(() => {
  const section = route.query.section;

  if (section && sectionTitles[section]) {
    selectedSection.value = section;
  }

  loadProfile();
});

function saveChanges() {
  console.log('💾 сохранить изменения');
}

function logoutConfirm() {
  showLogoutModal.value = true;
}

function logout() {
  const auth = getAuth();
  signOut(auth).then(() => {
    localStorage.clear();
    router.push('/');
  });
}



async function freezeAccount() {
  
  if (loadingFreeze.value) return;

  try {
    loadingFreeze.value = true;
    console.log("🧊 Замораживаем аккаунт...");
    const result = await UserService.freezeAccount();
    if (!result?.success) {
      throw new Error(
        result?.error ||
        "Не удалось заморозить аккаунт"
      );
    }
    console.log(
      "✅ Аккаунт успешно заморожен:",
      result
    );
    showFreezeModal.value = false;
    const auth = getAuth();
    await signOut(auth);
    localStorage.clear();
    await router.replace("/");
  } catch (error) {
    console.error(
      "❌ Ошибка заморозки аккаунта:",
      error
    );
    showSnackbarMessage(
      error?.message ||
      "Не удалось заморозить аккаунт"
    );
  } finally {
    loadingFreeze.value = false;
  }
}

const loadingDelete = ref(false);

function confirmDelete() {
  showDeleteModal.value = true;
}

async function deleteAccount() {
  try {
    loadingDelete.value = true;

    const userId = user.value.user_id;
    const token = await getAuth().currentUser.getIdToken();

    const success = await UserService.deleteUserAccount(userId, token);

    if (success) {
      console.log("✅ Аккаунт удалён");
      localStorage.clear();
      router.push('/');
    } else {
      console.error("❌ Не удалось удалить аккаунт");
      showSnackbarMessage("Ошибка при удалении аккаунта");
    }
  } catch (error) {
    console.error("❌ Ошибка:", error);
    showSnackbarMessage("Что-то пошло не так");
  } finally {
    loadingDelete.value = false;
  }
}

async function handleResumeUploadCompleted() {
  console.log(
    "✅ Резюме обработано — обновляем профиль"
  );

  showAIModal.value = false;
  isLoadingProfile.value = true;

  await loadProfile();

  console.log(
    "✅ Профиль обновлён — открываем резюме"
  );

  await router.replace({
    query: {
      ...route.query,
      section: "resume"
    }
  });

  showSnackbarMessage(
    "Резюме успешно обновлено ✨"
  );
}

function showSnackbarMessage(msg) {
  snackbarMessage.value = msg;
  showSnackbar.value = true;
  setTimeout(() => showSnackbar.value = false, 3000);
}

function openSupportChat() {
  showSupport.value = true;
}

function handleFreeze() {
  showFreezeModal.value = true;
}

function handleGeneralUpdated(updated) {
  console.log("🔥 Обновляем юзера в родителе:", updated);
  for (const key in updated) {
    user.value[key] = updated[key];
  }
  localStorage.setItem("user_profile", JSON.stringify(user.value));
  showSnackbarMessage("Профиль сохранён!");
}

const onCompanyRemoved = (removedId) => {
  console.log("🗑 Removing company from UI:", removedId);

  if (!removedId) {
    console.warn("⚠️ company-removed пришёл без companyId");
    return;
  }

  if (Array.isArray(user.value.companies)) {
    user.value.companies = user.value.companies.filter(
      company => company.company_id !== removedId
    );
  }

  if (user.value.current_company?.company_id === removedId) {
    user.value.current_company = null;
  }

  localStorage.setItem(
    "user_profile",
    JSON.stringify(user.value)
  );

  showSnackbarMessage("Компания удалена");
};

const handleOpenPaywall = () => {
  console.log("🚀 Открытие Paywall...");
  showPaywall.value = false;
  nextTick(() => {
    console.log("🔁 nextTick — установка showPaywall = true");
    showPaywall.value = true;
  });
};

function openCompanyDrawerRegular() {
  isPremiumModeForCompany.value = false;
  showCompanyDrawer.value = true;
}

function openCompanyDrawerPremium() {
  isPremiumModeForCompany.value = true;
  showCompanyDrawer.value = true;
}

function handleSkillsSaved() {
  localStorage.setItem("user_profile", JSON.stringify(user.value));
  showSnackbarMessage("Профессиональные скиллы сохранены");
}

async function handleAIClick1() {
  aiLoading1.value = true;

  try {
    console.log("✅ запускаем AI генерацию");
    const result = await PremiumService.fillInResumePremium();
    console.log("🤖 AI result:", result);
    user.value.aboutSelf = result.text;
    showSnackbarMessage("AI обновил раздел «О себе» ✨");
  } catch (e) {
    console.error(e);
    showSnackbarMessage("Ошибка при генерации AI 😢");
  } finally {
    aiLoading1.value = false;
    await loadProfile();
  }
}

async function handleAIClick2() {
  aiLoading2.value = true;
  try {
    console.log("✅ открыть upload");
    showAIModal.value = true;
  } catch (e) {
    console.error(e);
    showSnackbarMessage("Ошибка 😢");
  } finally {
    aiLoading2.value = false;
  }
}

</script>

<style scoped>

.page-wrapper {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.sticky-header {
  height: 60px;
  flex-shrink: 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
}

.sticky-footer {
  height: 60px;
  flex-shrink: 0;
  background: var(--profile_footer-bg);
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scrollable-content {
  flex: 1;
  margin-top: 60px;
  margin-bottom: 60px;
  overflow-y: auto;
}

[data-theme="light"] .scrollable-content  {
  background-color: rgb(240, 240, 240);
}

[data-theme="dark"] .scrollable-content {
  background-color: #161616;
}

.layout {
  display: flex;
  max-width: 1440px;
  margin: 0 auto;
  padding: 30px 20px;
}

.profile-main {
  flex: 1;
  min-width: 0;
  max-width: 800px;
  margin-top: 2px;
}

.floating-save-btn {
  position: fixed;
  top: 80px;
  right: calc(50% - 620px);
  background-color: #2173e5;
  color: white;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 16px;
  padding: 12px 24px;
  border: none;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 2000;
  transition: all 0.3s ease;
}

.snackbar {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: #2173e5; 
  color: white;
  padding: 10px 20px;
  border-radius: 12px;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 2000;
  opacity: 0.95;
}

.loading-spinner {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  color: #000;
  padding: 16px 28px;
  border-radius: 999px;
  font-family: "Eastman-Medium", sans-serif;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2000;
}

.profile-title {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 16px;
  margin-bottom: 10px;
  margin-top: 0px;
  color: #111;
}

html[data-theme="dark"] .profile-title {
  color: #f0f0f0;
}

.company-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.25);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
}

  .company-overlay .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(255,255,255,0.4);
    border-top-color: #2887F7;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .layout {
  display: flex;
  gap: 20px;
  }

  .profile-main {
  flex: 1;
  max-width: 800px;
  }

  .profile-right {
  width: 320px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 40px;
  }

  @media (max-width: 1200px) {
  .profile-right {
    display: none;
  }

}

</style> 